import React from 'react';
import { connect } from 'react-redux';
import { RootState } from 'types/state';
import { FormStateSubsectionsKeys, FormError } from '@redux/modules/form/types';
import DebugRender from 'components/Debug/Render/DebugRender';
import { FormErrorsUtil } from 'helpers/formErrors';
import { getDevSettings } from '@redux/modules/settings/selectors';
import { REQUIRED } from 'constants/errors';
import './FieldErrors.scss';
import { LocaleOptions } from 'helpers/types';
import { getSubsectionErrorsByAttribute } from '@redux/modules/form/selectors';
import { DataAttributeType } from 'helpers/attributes/types';
import { AttributeAnchors } from 'components/Anchor/Anchor';
import { i18n } from 'config/locales';
import { AppKeydownWatchers } from 'util/singletons';
import { ErrorMessageItem } from './ErrorMessageItem/ErrorMessageItem';
import { FieldError } from './FieldError';
import SvgComponent from '../../SvgComponent';

interface OwnProps {
  /**
   * Filters formError by a matching attribute.
   */
  attribute: DataAttributeType

  /**
   * By default, error filtering is based on whether or not the error contains
   * the attribute. To change this, you can enable "exact" to be true and it'll
   * filter errors to have the exact attribute.
   */
  exact?: boolean
  subsectionKey: FormStateSubsectionsKeys
  showRequiredErrors: boolean
  displayErrorsReviewOnly?: string[]

  /**
   * ShowVerbose is a debug property. When enabled, this will show verbose data
   * associated to the error. This is meant to be used with
   * State.settings.development.showFieldErrorVerbose
   *
   * * Default: false
   */
  showVerbose: boolean
}

/**
 * FieldErrorProps.
 */
export type Props = OwnProps & ReturnType<typeof mapStateToProps> // eslint-disable-line no-use-before-define

/**
 * This will compare both parameters and look for differences.
 */
const doesParametersMatch = (
  oldParameters?: Record<string, any>,
  newParameters?: Record<string, any>
) => {
  let match = true;

  if (oldParameters && newParameters) {
    const keys = Object.keys(oldParameters);

    keys.forEach((key) => {
      if (match === false) { return; }
      if (oldParameters[key] !== newParameters[key]) { match = false; }
    });
  } else if (oldParameters !== newParameters) { match = false; }

  return match;
};

/**
 * This will compare both localeOptions and look for differences.
 */
const doesLocaleOptionsMatch = (
  oldLocaleOptions?: LocaleOptions,
  newLocaleOptions?: LocaleOptions
) => {
  let match = true;

  if (oldLocaleOptions && newLocaleOptions) {
    const keys = ['titleParameters', 'messageParameters', 'noteParameters'];

    keys.forEach((argKey) => {
      if (match === false) { return; }

      const key = argKey as keyof LocaleOptions;
      const keyMatch = doesParametersMatch(oldLocaleOptions[key], newLocaleOptions[key]);

      if (keyMatch === false) { match = false; }
    });
  } else if (oldLocaleOptions !== newLocaleOptions) { match = false; }

  return match;
};

interface State {
  showDetailedErrors?: boolean;
}

class FieldErrors extends React.Component<Props, State> {
  static defaultProps: Partial<Props> = {
    exact: false,
    showVerbose: false,
    displayErrorsReviewOnly: []
  };

  errorDebuggerState = false;

  constructor(props: Props) {
    super(props);
    this.state = {
      showDetailedErrors: false,
    };
    this.toggleDetailedErrors = this.toggleDetailedErrors.bind(this);
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    // This minimizes the updates whenever formErrors is updated. Without this,
    // anything subscribed to the formErrors will 'always' update. This can be
    // problematic with the more you have. The below logic will do comparisons
    // for each object and look for differences. One item to note: Rendering can
    // be more expensive than calculations.
    let shouldUpdate = false;

    if (nextProps.errors.length === this.props.errors.length) {
      nextProps.errors.forEach((newError) => {
        if (shouldUpdate === true) {
          return;
        }

        const oldMatchingError = this.props.errors.find(
          (e) => e.attribute === newError.attribute && e.code === newError.code
        );

        if (oldMatchingError) {
          const match = doesLocaleOptionsMatch(
            oldMatchingError.localeOptions,
            newError.localeOptions
          );

          if (match === false) {
            shouldUpdate = true;
          }
        } else {
          shouldUpdate = true;
          return true; // eslint-disable-line consistent-return
        }
      });
    } else {
      shouldUpdate = true;
    }
    const detailedErrorOption = AppKeydownWatchers.getInstance().getAppToggleState().errorDebugger;
    if (nextState.showDetailedErrors !== this.state.showDetailedErrors || detailedErrorOption !== this.errorDebuggerState) {
      this.errorDebuggerState = detailedErrorOption;
      shouldUpdate = true;
    }

    return shouldUpdate;
  }

  getFullErrorsOnReview = (): FormError[] => {
    const errors = FormErrorsUtil(this.props.errors).handleShowRequiredErrors(
      this.props.showRequiredErrors,
      this.props.displayErrorsReviewOnly
    );
    return errors;
  };

  toggleDetailedErrors = (event: React.MouseEvent<HTMLButtonElement>): void => {
    const toggleValue = !this.state.showDetailedErrors; // eslint-disable-line react/no-access-state-in-setstate
    this.setState({ showDetailedErrors: toggleValue });
  };

  cleanErrorText = (text: string): string => {
    let cleanText = text;
    if (cleanText.includes('List.items[')) {
      cleanText = cleanText.replace(/List.items\[(\d+)\]\.Item\./, 'List Item $1 ');
      cleanText = cleanText.replace(/(\d+)/, (val) => (parseInt(val, 10) + 1).toString());
    }
    return cleanText;
  };

  getFilteredErrors = (): FormError[] => {
    const errors = FormErrorsUtil(this.props.errors).handleShowRequiredErrors(
      this.props.showRequiredErrors,
      this.props.displayErrorsReviewOnly
    );

    // Removing duplicate error messages for date year min and date min
    let filteredErrors = errors;
    filteredErrors.forEach((error) => {
      if (error.code === 'error.date.min'
            || error.code === 'error.taxesSatisfied.min') {
        filteredErrors = filteredErrors.filter(
          (err) => !err.code.includes('error.date.year.min')
        );
      }
      if (error.code === 'error.additionalFuture' || error.code === 'history.education.error.afterDuration') {
        filteredErrors = filteredErrors.filter(
          (err) => !err.code.includes('error.date.year.max')
        );
      }
    });
    // When showRequiredErrors is true, this will go through the list and return
    // only one "REQUIRED" error, versus all of them. This will hide all the
    // remaining errors as well (which should reappear when REQUIRED goes away).
    if (this.props.showRequiredErrors === true) {
      const required = filteredErrors.find((error) => error.code.includes(REQUIRED));

      if (required) { return [required]; }
    }

    // This handles unique codes
    const uniqueMap = new Map();
    const uniqueErrors: FormError[] = [];

    filteredErrors.forEach((e) => {
      const errorString = e.code;
      if (!uniqueMap.has(errorString)) {
        uniqueMap.set(errorString, true);
        uniqueErrors.push(e);
      }
    });

    return uniqueErrors;
  };

  render() {
    const { showVerbose } = this.props;
    const errors = this.getFilteredErrors();
    const attributes = FormErrorsUtil(this.props.errors).getUniqueAttributes();
    const allErrors = this.getFullErrorsOnReview();

    const showErrorOption = AppKeydownWatchers.getInstance().getAppToggleState().errorDebugger;
    const showHiddenError = (this.state.showDetailedErrors === true);
    const hasHiddenError = allErrors.length > 0 && this.props.showRequiredErrors === true;

    const textButtonStyle: React.CSSProperties = {
      border: 'none',
      backgroundColor: 'transparent',
      color: 'black',
      padding: '0px',
      textAlign: 'center',
      textDecoration: 'none',
      display: 'inline-block',
      fontSize: '14px',
      margin: '0px',
      transitionDuration: '0.4s',
      cursor: 'pointer',
    };
    const svgButtonStyle: React.CSSProperties = {
      border: 'none',
      backgroundColor: 'transparent',
      padding: '0px',
      textAlign: 'center',
      textDecoration: 'none',
      display: 'inline-block',
      fontSize: '14px',
      margin: '0px',
      transitionDuration: '0.4s',
      cursor: 'pointer',
      width: '16px'
    };
    return (
      <>
        <DebugRender parent={this} />
        {errors.length > 0 && (
          <FieldError guid="0">
            <AttributeAnchors attribute={attributes} prefix="error" />

            {errors.map((error) => (
              <React.Fragment key={error.code}>
                {showVerbose && (
                <div className="verbose" key={error.code}>
                  <div className="verbose-msg">
                    {error.attribute} - {error.code}
                  </div>
                  <ErrorMessageItem localeId={error.code} localeOptions={error.localeOptions} />
                </div>
                )}

                {!showVerbose && (
                <ErrorMessageItem localeId={error.code} localeOptions={error.localeOptions} />
                )}
              </React.Fragment>
            ))}
            {hasHiddenError && showHiddenError
              && (
              <button className="chevron-up" style={svgButtonStyle} onClick={this.toggleDetailedErrors} title="Hide technical error details" type="button">
                <SvgComponent className="chevron-up" src="/img/chevron-up.svg" alt="Up Arrow" />
              </button>
              )}
            {hasHiddenError && !showHiddenError
              && (
              <button className="chevron-down" style={svgButtonStyle} onClick={this.toggleDetailedErrors} title="Show technical error details" type="button">
                <SvgComponent className="chevron-down" src="/img/chevron-down.svg" alt="Down Arrow" />
              </button>
              )}
            {(showErrorOption || showHiddenError) && hasHiddenError && (
            <button
              onClick={this.toggleDetailedErrors}
              type="button"
              style={textButtonStyle}
            >
              {showHiddenError ? ' Hide Technical Errors ' : 'Show Technical Errors '}
            </button>
            )}
            { hasHiddenError && showHiddenError

            && allErrors.map((error) => (
              <React.Fragment key={`allErrors${error.code}`}>
                <div style={{ width: '100%' }}>
                  <div>
                    {this.cleanErrorText(error.attribute)} - {i18n.t(`${error.code}.title`)}
                  </div>
                </div>
              </React.Fragment>
            ))}

          </FieldError>
        )}
      </>
    );
  }
}

const mapStateToProps = (state: RootState, ownProps: OwnProps) => ({
  errors: getSubsectionErrorsByAttribute(
    state,
    ownProps.subsectionKey,
    ownProps.attribute,
    ownProps.exact
  ),
  showVerbose: getDevSettings(state).showFieldErrorVerbose,
});

export const ConnectedFieldErrors: any = connect(mapStateToProps)(FieldErrors);

/**
 * For testing only.
 */
export const internalTestingFieldErrors = {
  FieldErrors,
  doesLocaleOptionsMatch,
  doesParametersMatch,
};
